import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

interface PrivateRouteProps {
  children: React.ReactNode;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const { user, loading, fetchUserProfile, isAuthenticated } = useAuth();
  const location = useLocation();
  const [isCheckingSession, setIsCheckingSession] = useState(false);
  const [sessionCheckComplete, setSessionCheckComplete] = useState(false);

  useEffect(() => {
    const checkSession = async () => {
      try {
        console.log('Checking session in PrivateRoute');
        setIsCheckingSession(true);
        
        const { data } = await supabase.auth.getSession();
        
        if (data.session?.user) {
          console.log('Session found but no user, fetching profile');
          await fetchUserProfile(data.session.user.id);
        } else {
          console.log('No session found');
        }
      } catch (error) {
        console.error('Error checking session:', error);
        toast.error('Authentication error. Please sign in again.');
      } finally {
        setIsCheckingSession(false);
        setSessionCheckComplete(true);
      }
    };

    if (!loading && !isAuthenticated) {
      checkSession();
    } else {
      setSessionCheckComplete(true);
    }
  }, [user, loading, fetchUserProfile, isAuthenticated]);

  // Show loading state while initial auth check is in progress
  if (loading || isCheckingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 text-primary animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Verifying your authentication...</p>
        </div>
      </div>
    );
  }

  // If no user after session check is complete, redirect to login
  if (sessionCheckComplete && !isAuthenticated) {
    console.log('No user found, redirecting to login');
    // Save the attempted URL for redirect after login
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  console.log('User authenticated, rendering protected content');
  return <>{children}</>;
};

export default PrivateRoute;